import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useNavigation } from 'react-router';

import { useCurrentUser } from '@/api/use-current-user';
import { EmptyState } from '@/components/empty-state';
import { PageSkeleton } from '@/components/error-pages';
import { Button } from '@/components/orno/button';
import { Drawer } from '@/components/orno/drawer';
import { Icon } from '@/components/orno/icon';
import { OverlayProvider, useOverlayLayer } from '@/components/overlay-context';
import { ThemeToggle } from '@/theme/theme-toggle';

import type { NavItem } from '../routing';
import { Nav } from './nav';
import { UserMenu } from './user-menu';

/**
 * The application frame, in orno's shell: the rail with the brand, the navigation and the theme switch (a drawer
 * below 900 px), a bar with the account menu, and the routed page. The frame waits for the signed-in person: a 401
 * has already sent the browser to Servo's sign-in.
 */
export function AppShell({ navItems }: { navItems: NavItem[] }) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { user, error, retry } = useCurrentUser();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const { layer, hostRef, inert } = useOverlayLayer();
  const { enter } = layer;

  // The navigation drawer is one of the overlays: the page is inert while it is open, and the menu button takes focus
  // back once it closes.
  useEffect(() => (mobileNavOpen ? enter(menuButton.current) : undefined), [mobileNavOpen, enter]);

  if (error !== undefined) {
    return (
      <div className="narrow-page">
        <EmptyState
          icon="error"
          title={t('errors.loadFailedTitle')}
          description={t('errors.network')}
          action={
            <Button variant="acc" onClick={retry}>
              {t('actions.retry')}
            </Button>
          }
        />
      </div>
    );
  }
  if (user === undefined) return <PageSkeleton />;

  return (
    <div className="shell">
      {/* While a drawer or panel is open the rest of the page is inert: focus and the screen reader stay in it. */}
      <a href="#main" className="skip-link" inert={inert}>
        {t('actions.skipToContent')}
      </a>

      <aside className="shell__rail" inert={inert}>
        <div>
          <Link to="/" className="shell__brand">
            {t('appName')}
          </Link>
          <Nav items={navItems} />
          <div className="shell__rail-foot">
            <ThemeToggle />
          </div>
        </div>
      </aside>

      <div className="shell__body" inert={inert}>
        <header className="shell__top">
          <button
            ref={menuButton}
            type="button"
            className="btn-icon app-mobile-only"
            aria-label={t('actions.openNavigation')}
            onClick={() => setMobileNavOpen(true)}
          >
            <Icon name="menu" size={18} />
          </button>
          <Link to="/" className="app-brand app-mobile-only">
            {t('appName')}
          </Link>
          <span className="grow" />
          <UserMenu user={user} />
        </header>

        {navigation.state === 'loading' ? (
          <div role="progressbar" aria-label={t('actions.loading')} className="app-progress" />
        ) : null}

        <main id="main" tabIndex={-1} className="shell__main">
          <OverlayProvider value={layer}>
            <Outlet />
          </OverlayProvider>
        </main>
      </div>

      {/* Beside the page, not in its bar; a section, so the <header> orno writes into the drawer is not a banner. */}
      <section className="app-drawer">
        <Drawer
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          heading={t('appName')}
          closeLabel={t('actions.close')}
          side="left"
        >
          <div className="stack app-drawer-nav">
            <Nav items={navItems} onNavigate={() => setMobileNavOpen(false)} />
            <ThemeToggle />
          </div>
        </Drawer>
      </section>

      {/* The pages' panels open here, beside the page that turns inert under them. */}
      <section className="app-drawer" ref={hostRef} />
    </div>
  );
}
